import React, { useEffect, useState } from 'react';
import {
  fetchAnalyticsOverview, fetchTicketTrends, fetchCategoryPriority,
  fetchSLAStats, fetchAgentLeaderboard, fetchBotContainment, fetchSatisfactionAnalytics
} from '../../api/analyticsApi';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './AnalyticsDashboard.css';

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState({});
  const [trends, setTrends] = useState({});
  const [categoryPriority, setCategoryPriority] = useState({});
  const [sla, setSLA] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [bot, setBot] = useState({});
  const [satisfaction, setSatisfaction] = useState({});

  useEffect(() => {
    Promise.all([
      fetchAnalyticsOverview(),
      fetchTicketTrends(30),
      fetchCategoryPriority(),
      fetchSLAStats(),
      fetchAgentLeaderboard(),
      fetchBotContainment(),
      fetchSatisfactionAnalytics()
    ]).then(([ov, tr, cp, sla, lb, bt, sat]) => {
      setOverview(ov); setTrends(tr); setCategoryPriority(cp);
      setSLA(sla); setLeaderboard(lb); setBot(bt); setSatisfaction(sat);
    });
  }, []);

  return (
    <div className="analytics-dashboard">
      <h1>Analytics Dashboard</h1>
      {/* Hero cards */}
      <div className="analytics-cards-row">
        <div className="card">Total Tickets<br /><b>{overview.total_tickets}</b></div>
        <div className="card">Open<br /><b>{overview.open_tickets}</b></div>
        <div className="card">Closed<br /><b>{overview.closed_tickets}</b></div>
        <div className="card">Avg Response Time<br /><b>{overview.avg_response_time_minutes} min</b></div>
        <div className="card">Avg Resolution Time<br /><b>{overview.avg_resolution_time_hours} hrs</b></div>
        <div className="card">Avg Satisfaction<br /><b>{overview.avg_satisfaction} / 5</b></div>
      </div>

      {/* Trends */}
      <div className="analytics-charts-row">
        <div className="chart-panel">
          <h3>Ticket Volume (30 Day)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart>
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip />
              <Legend />
              <Line type="monotone" name="Created" dataKey="count" data={trends.created || []} stroke="#667eea" strokeWidth={2} />
              <Line type="monotone" name="Closed" dataKey="count" data={trends.closed || []} stroke="#4caf50" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-panel">
          <h3>By Category</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={categoryPriority.by_category || []} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" label />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-panel">
          <h3>By Priority</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={categoryPriority.by_priority || []}>
              <XAxis dataKey="priority" />
              <YAxis />
              <Bar dataKey="count" fill="#ffc658" />
              <Tooltip />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="analytics-charts-row">
        <div className="chart-panel">
          <h3>SLA Compliance</h3>
          <p>Met: <b>{sla.sla_met}</b> | Breached: <b>{sla.sla_breached}</b> | Compliance Rate: <b>{sla.sla_compliance_rate}%</b></p>
        </div>
        <div className="chart-panel">
          <h3>CSAT Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={satisfaction.trend || []}>
              <XAxis dataKey="date" />
              <YAxis domain={[0,5]} />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip />
              <Legend />
              <Line type="monotone" name="CSAT" dataKey="score" stroke="#2196f3" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-panel">
          <h3>Bot Containment</h3>
          <p>By Bot: <b>{bot.bot_solved}</b> / <b>{bot.total_tickets}</b></p>
          <p>Containment Rate: <b>{bot.containment_rate}%</b></p>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="analytics-leaderboard">
        <h2>Agent Leaderboard</h2>
        <table>
          <thead>
            <tr>
              <th>Rank</th><th>Agent</th><th>Tickets Resolved</th><th>Avg Rating</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((a,i)=>(
              <tr key={a.agent}>
                <td>{i+1}</td>
                <td>{a.agent}</td>
                <td>{a.tickets_resolved}</td>
                <td>{a.avg_rating || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add more widgets: sentiment, unassigned, trending tags, etc as needed! */}
    </div>
  );
}
